import React, { useRef } from "react";
import styled, { keyframes, css } from "styled-components";

/**
 * A Hogwarts ghost drifting across whatever it is placed in.
 *
 * Two tones, because the ghosts have to suit their surface:
 *   "ink"      — sepia line-work, for the Marauder's Map parchment
 *   "spectral" — pale translucent blue, for the dark sections
 *
 * The parent needs `position: relative`; the ghost spans it edge to edge.
 * Drift is animated on `left`, not `translateX` — percentage transforms
 * resolve against the element's own width, which barely moves it.
 */

export type GhostTone = "ink" | "spectral";

interface Props {
  name?: string;
  tone?: GhostTone;
  /** seconds for one crossing */
  duration?: number;
  /** seconds before the first crossing begins */
  delay?: number;
  /** start partway across instead of always at the left edge (default true) */
  randomStart?: boolean;
  /** vertical band, as a percentage of the parent */
  band?: [number, number];
  className?: string;
}

export const DriftingGhost: React.FC<Props> = ({
  name,
  tone = "spectral",
  duration = 20,
  delay = 0,
  randomStart = true,
  band = [14, 30],
  className,
}) => {
  /* A negative animation-delay starts the drift partway through, so ghosts
     don't all appear at the left edge together. Held in a ref so re-renders
     don't teleport them. */
  const offset = useRef(randomStart ? -Math.random() * duration : delay);

  return (
  <Wrap $tone={tone} $d={duration} $delay={offset.current} $a={band[0]} $b={band[1]} className={className}>
    <svg viewBox="0 0 60 70" aria-hidden="true">
      <path d="M30 4c-12 0-20 9-20 21v34c0 3 3 4 5 2l5-5 5 5c1.5 1.5 3.5 1.5 5 0l5-5 5 5c2 2 5 1 5-2V25C45 13 42 4 30 4z" />
      <ellipse cx="23" cy="24" rx="2.6" ry="4" className="feature" />
      <ellipse cx="37" cy="24" rx="2.6" ry="4" className="feature" />
      <ellipse cx="30" cy="37" rx="3" ry="4.4" className="feature" />
    </svg>
    {name && <Name $tone={tone}>{name}</Name>}
  </Wrap>
  );
};

export default DriftingGhost;

/* ==================== styles ==================== */

const bob = keyframes`
  0%, 100% { transform: translateY(0) rotate(-3deg); }
  50%      { transform: translateY(-9px) rotate(3deg); }
`;

const drift = (a: number, b: number) => keyframes`
  0%   { opacity: 0; left: -6%;  top: ${a}%; }
  7%   { opacity: 1; }
  35%  { top: ${b}%; }
  65%  { top: ${a}%; }
  92%  { opacity: 1; }
  100% { opacity: 0; left: 100%; top: ${(a + b) / 2}%; }
`;

const Wrap = styled.div<{ $tone: GhostTone; $d: number; $delay: number; $a: number; $b: number }>`
  position: absolute;
  left: -6%;
  top: ${({ $a }) => $a}%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  animation: ${({ $a, $b }) => drift($a, $b)} ${({ $d }) => $d}s linear infinite;
  animation-delay: ${({ $delay }) => $delay}s;

  svg {
    /* Some parents absolutely position every descendant svg — opt out so the
       ghost stays in flow above its label. */
    position: static;
    inset: auto;
    width: 42px;
    height: 50px;
    animation: ${bob} 5s ease-in-out infinite;

    ${({ $tone }) => $tone === "ink"
      ? css`
          fill: rgba(96, 76, 44, 0.52);
          stroke: rgba(58, 42, 18, 0.78);
          stroke-width: 1.8;
          .feature { fill: rgba(58, 42, 18, 0.6); stroke: none; }
        `
      : css`
          fill: rgba(186, 214, 244, 0.34);
          stroke: rgba(206, 230, 255, 0.42);
          stroke-width: 1.2;
          filter: drop-shadow(0 0 14px rgba(170, 205, 245, 0.4));
          .feature { fill: rgba(10, 20, 40, 0.5); stroke: none; }
        `}
  }

  @media (max-width: 700px) {
    svg { width: 28px; height: 33px; }
  }
`;

const Name = styled.span<{ $tone: GhostTone }>`
  margin-top: 0.28rem;
  font-family: "Caveat", cursive;
  font-size: 0.82rem;
  white-space: nowrap;
  color: ${({ $tone }) => ($tone === "ink" ? "rgba(60,44,18,0.9)" : "rgba(196,220,248,0.5)")};

  @media (max-width: 700px) { font-size: 0.68rem; }
`;
